'use strict'

import { getAdsDecision, isPremiumCardRoute } from './adsPolicy.js'

const ADS_CLIENT = 'ca-pub-1610284649584943'
const ADSENSE_SCRIPT_ID = 'sharewell-adsense-script'
const ADSENSE_BLOCK_STYLE_ID = 'sharewell-premium-ad-block-style'
const ADSENSE_SCRIPT_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADS_CLIENT}`

let currentRoute = null
let currentCard = null
let hasCardContext = false
let scriptPromise = null
let cleanupTimers = []

function hasDom() {
  return typeof document !== 'undefined' && typeof window !== 'undefined'
}

function ensureAdBlockStyle() {
  if (!hasDom() || document.getElementById(ADSENSE_BLOCK_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = ADSENSE_BLOCK_STYLE_ID
  style.textContent = `
    body.ads-disabled-for-premium-card ins.adsbygoogle,
    body.ads-disabled-for-premium-card iframe[id^="aswift_"],
    body.ads-disabled-for-premium-card iframe[id^="google_ads_frame"],
    body.ads-disabled-for-premium-card iframe[src*="googlesyndication.com"],
    body.ads-disabled-for-premium-card iframe[src*="googleads.g.doubleclick.net"],
    body.ads-disabled-for-premium-card div[id^="aswift_"],
    body.ads-disabled-for-premium-card div[id^="google_ads_iframe"] {
      display: none !important;
      visibility: hidden !important;
    }
  `
  document.head.appendChild(style)
}

function setPremiumAdBlockClass(disabled) {
  if (!hasDom() || !document.body) return
  ensureAdBlockStyle()
  document.body.classList.toggle('ads-disabled-for-premium-card', Boolean(disabled))
}

function getAdNodes() {
  if (!hasDom()) return []
  return Array.from(document.querySelectorAll([
    'ins.adsbygoogle',
    'iframe[id^="aswift_"]',
    'iframe[id^="google_ads_frame"]',
    'iframe[src*="googlesyndication.com"]',
    'iframe[src*="googleads.g.doubleclick.net"]',
    'div[id^="aswift_"]',
    'div[id^="google_ads_iframe"]',
  ].join(',')))
}

export function removeAutoAds() {
  getAdNodes().forEach(el => el.remove())
}

function clearCleanupTimers() {
  if (typeof window === 'undefined') {
    cleanupTimers = []
    return
  }
  cleanupTimers.forEach(timer => window.clearTimeout(timer))
  cleanupTimers = []
}

export function sweepAutoAds() {
  if (!hasDom()) return
  setPremiumAdBlockClass(true)
  clearCleanupTimers()
  ;[0, 250, 750, 1500, 3000].forEach(delay => {
    const run = () => removeAutoAds()
    if (delay === 0) {
      run()
    } else {
      cleanupTimers.push(window.setTimeout(run, delay))
    }
  })
}

function getCurrentDecision(route = currentRoute, options = {}) {
  return getAdsDecision(route, {
    card: Object.prototype.hasOwnProperty.call(options, 'card') ? options.card : currentCard,
    hasCardContext: Object.prototype.hasOwnProperty.call(options, 'hasCardContext')
      ? options.hasCardContext
      : hasCardContext,
  })
}

function shouldWaitForCardContext(route) {
  return isPremiumCardRoute(route) && !hasCardContext
}

function ensureAdsScriptLoaded() {
  if (!hasDom()) return Promise.resolve(false)
  if (document.getElementById(ADSENSE_SCRIPT_ID)) return Promise.resolve(true)
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = ADSENSE_SCRIPT_ID
    script.async = true
    script.src = ADSENSE_SCRIPT_SRC
    script.crossOrigin = 'anonymous'
    script.onload = () => resolve(true)
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('Failed to load AdSense script'))
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

async function applyCurrentDecision() {
  const decision = getCurrentDecision()
  if (!decision.allowed) {
    sweepAutoAds()
    return false
  }

  setPremiumAdBlockClass(false)
  clearCleanupTimers()
  return true
}

export function setAdContext(context = {}) {
  if (Object.prototype.hasOwnProperty.call(context, 'route')) {
    currentRoute = context.route
  }
  if (Object.prototype.hasOwnProperty.call(context, 'card')) {
    currentCard = context.card || null
    hasCardContext = Boolean(context.hasCardContext || context.card)
  } else if (Object.prototype.hasOwnProperty.call(context, 'hasCardContext')) {
    hasCardContext = Boolean(context.hasCardContext)
  }

  return applyCurrentDecision()
}

export function clearAdContext() {
  currentCard = null
  hasCardContext = false
  return applyCurrentDecision()
}

export async function requestAdSlotLoad(route, options = {}) {
  const decision = getCurrentDecision(route, options)
  if (!decision.allowed) {
    sweepAutoAds()
    return false
  }

  setPremiumAdBlockClass(false)
  try {
    await ensureAdsScriptLoaded()
    ;(window.adsbygoogle = window.adsbygoogle || []).push({})
    return true
  } catch {
    // AdSense not available; fail silently to avoid breaking the app.
    return false
  }
}

export function setupAutoAdsManager(router) {
  if (!router || typeof router.afterEach !== 'function') return

  const setRoute = (route) => {
    currentRoute = route
    currentCard = null
    hasCardContext = false
    if (shouldWaitForCardContext(route)) {
      sweepAutoAds()
      return
    }
    applyCurrentDecision()
  }

  setRoute(router.currentRoute.value)

  router.afterEach((to) => {
    setRoute(to)
  })
}
